import { useState } from 'react';

import type { EventResponse, TopicResponse } from '@lifetimeline/shared';

import { Timeline } from './Timeline.js';
import { useI18n } from '../i18n/index.js';
import styles from './TimelineWithFilter.module.css';

interface Props {
  topics: readonly TopicResponse[];
  events: readonly EventResponse[];
  onEventClick?: (event: EventResponse) => void;
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

/**
 * 주제로 걸러 보는 타임라인.
 *
 * 기존 Life Timeline에도 있던 것이다. 인생이 쌓이면 축 하나에 전부 올라와
 * "일 이야기만 보고 싶다"가 안 된다.
 *
 * **"전체"가 기본이고 항상 첫 자리**다. 걸러 놓은 것을 잊고 "왜 안 보이지"
 * 하는 일이 흔한데, 돌아갈 자리가 늘 같은 곳에 있어야 한다.
 *
 * 주제가 하나뿐이면 그리지 않는다 — 고를 것이 없는 고르개다.
 *
 * ⚠️ 칩에 **개수를 적지 않는다.** 고치는 화면의 칩에는 없는 숫자라, 남의
 * 것을 보다 내 것으로 오면 같은 줄이 다른 것으로 보였다. 게다가 한 쪽만
 * 불러온 상태에서는 셀 수 있는 수가 애초에 거짓이었다.
 *
 * **읽기 전용이다.** 걸러 보는 중에는 순서를 못 바꾼다 — 화면에 그 해의
 * 일부만 있는데 그 순서를 저장하면 안 보이는 기록이 자리를 잃는다. 그래서
 * 고치는 화면(내 프로필)은 필터 없이 `Timeline`을 바로 쓰고, 여기는 남의
 * 타임라인을 훑어보는 데만 쓴다.
 */
export function TimelineWithFilter({
  topics,
  events,
  onEventClick,
  onLoadMore,
  loadingMore = false,
}: Props) {
  const { t } = useI18n();
  const [active, setActive] = useState<string | null>(null);

  return (
    <>
      {topics.length > 1 ? (
        <ul className={styles.filter}>
          <li>
            <button
              type="button"
              className={styles.chip}
              aria-pressed={active === null}
              onClick={() => setActive(null)}
            >
              {t('timeline.all')}
            </button>
          </li>
          {topics.map((topic) => (
            <li key={topic.topicId}>
              <button
                type="button"
                className={styles.chip}
                aria-pressed={active === topic.topicId}
                style={
                  active === topic.topicId
                    ? { background: topic.color, borderColor: topic.color, color: '#fff' }
                    : { borderColor: topic.color, color: topic.color }
                }
                onClick={() => setActive(topic.topicId)}
              >
                {topic.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <Timeline
        topics={topics}
        events={events}
        activeTopicId={active}
        {...(onEventClick === undefined ? {} : { onEventClick })}
        {...(onLoadMore === undefined ? {} : { onLoadMore, loadingMore })}
      />
    </>
  );
}
